import { HttpRequest } from '@angular/common/http';
import { AppConfiguration } from '../../services/configuration/app.configuration';

export function isGoogleApiRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes('googleapis.com');
}

export function isRunwayerApiRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes('https://app.runwayer.com/');
}

export function isStripeRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes('api.stripe');
}

export function isExternalRequest(req: HttpRequest<unknown>): boolean {
  return isGoogleApiRequest(req) || isRunwayerApiRequest(req) || isStripeRequest(req);
}

export function shouldAttachStretchitHeaders(req: HttpRequest<unknown>): boolean {
  return !isExternalRequest(req);
}

export function shouldAttachAccessToken(req: HttpRequest<unknown>, accessToken: string): boolean {
  return Boolean(accessToken) && shouldAttachStretchitHeaders(req) && !isTokenExchangeEndpoint(req);
}

export function getHostFromUrl(url: string): string {
  const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^/:]+)/);

  return match?.[1] ?? '';
}

export function isStretchitHostRequest(req: HttpRequest<unknown>): boolean {
  return getHostFromUrl(req.url).includes('stretchit') || req.url.startsWith('/');
}

export function isTokenExchangeEndpoint(req: HttpRequest<unknown>): boolean {
  return (
    req.url.includes('/auth/token') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/logout')
  );
}

export function isAuthRecoveryExcludedEndpoint(req: HttpRequest<unknown>): boolean {
  return (
    isTokenExchangeEndpoint(req) ||
    /\/auth\/(login|register|social|password\/(?:reset|change))(?:[/?#]|$)/.test(req.url)
  );
}

export function isUserProfileSnapshotRequest(
  req: HttpRequest<unknown>,
  config: AppConfiguration
): boolean {
  if (req.method !== 'GET') return false;

  try {
    const url = new URL(req.url);

    return url.origin === getHttpOrigin(config.apiNewBaseUrl) && url.pathname === '/api/v2/user';
  } catch {
    return false;
  }
}

export function isConfiguredStretchitApiRequest(
  req: HttpRequest<unknown>,
  config: AppConfiguration
): boolean {
  if (isRelativeUrl(req.url)) return true;

  const requestOrigin = getHttpOrigin(req.url);
  if (requestOrigin === null) return false;

  const configuredOrigins = [
    config.baseURL,
    config.authBaseUrl,
    config.apiNewBaseUrl,
    config.apiPaymentsBaseUrl,
    config.programsNewUrl,
  ]
    .filter(Boolean)
    .map(getHttpOrigin)
    .filter((origin): origin is string => origin !== null);

  return configuredOrigins.includes(requestOrigin);
}

export function isConfiguredStretchitBusinessApiRequest(
  req: HttpRequest<unknown>,
  config: AppConfiguration
): boolean {
  if (!isConfiguredStretchitApiRequest(req, config) || isAuthRecoveryExcludedEndpoint(req)) {
    return false;
  }

  if (isRelativeUrl(req.url)) {
    return new URL(req.url, 'https://stretchit.local').pathname.startsWith('/api/');
  }

  return true;
}

function isRelativeUrl(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

function getHttpOrigin(url: string): string | null {
  try {
    const parsed = new URL(url);

    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.origin : null;
  } catch {
    return null;
  }
}

export function isConfiguredAuthRequest(
  req: HttpRequest<unknown>,
  config: AppConfiguration
): boolean {
  const authOrigin = getHttpOrigin(config.authBaseUrl);
  return authOrigin !== null && getHttpOrigin(req.url) === authOrigin;
}
